import React, { useMemo } from 'react';
import { DailyMeetingRoomSchedule } from "./DailyMeetingRoomSchedule";
import { TimeSwimlane } from "./TimeSwimlane";
import {
  HALF_HOUR_TIME_SLOT_HEIGHT,
  MEETING_ROOM_HEADER_HEIGHT,
  MEETING_ROOM_SWIMLANE_WIDTH
} from "./StructuralComponents";
import { MeetingRoom } from "../models/MeetingRooms";
import { useSelector } from "react-redux";
import { isLoading, selectedFloorId, selectFilteredMeetingRooms } from "../models/room-finder.duck";
import { NoMeetingRoomsInOfficeMessage } from "./NoMeetingRoomsInOfficeMessage";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Grid } from "./meeting-room-grid/StructuralComponents";
import styled from "styled-components";
import moment from "moment";
import { SyncStatus } from '@shared/hooks/WithMicrosoftCalendarSyncActiveChecker';
import { selectActiveDay } from '@features/whereabouts/store/editMovements.duck';
import { isOnSameDay } from '@shared/utils/DateUtils';
import { findOfficeInOffices } from '@shared/utils/OfficeHelper';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';

export function MeetingRoomScheduleList(props: Props) {
  const {calendarSyncStatus} = props;
  const meetingRooms = useSelector(selectFilteredMeetingRooms);
  const meetingRoomsLoading = useSelector(isLoading);
  const activeDate = useSelector(selectActiveDay);
  const allOffices = useSelector(selectAllOfficeEntities);
  const floorId = useSelector(selectedFloorId);

  const percentageThroughDay = useMemo(() => {
    const mmt = moment();
    return (mmt.hour() * 60 + mmt.minute()) / 1440;
  }, []);

  const isViewingToDay = useMemo(() => {
    return isOnSameDay(moment(), activeDate);
  }, [activeDate]);

  const sortedMeetingRooms = useMemo(() => {
    let rooms = floorId
      ? meetingRooms.filter((mr) => mr.officeId === floorId)
      : meetingRooms;
    let toSort = rooms.map((meetingRoom) => ({
      ...meetingRoom,
      officeLabel: findOfficeInOffices(allOffices, meetingRoom.officeId)?.label,
    }));
    let sortedByName = naturalSort(toSort, 'name');
    return naturalSort(sortedByName, 'officeLabel')
  }, [allOffices, meetingRooms, floorId])

  return (
    <>
      {meetingRoomsLoading && <LoadingSpinner fullScreen={true} hideText={true} />}
      {sortedMeetingRooms.length === 0 ? <>
        <NoMeetingRoomsInOfficeMessage />
      </> : <>
        <>
          <GridContainer style={{height: '100%'}}>
            <Grid>
                <TimeSwimlane />
                {sortedMeetingRooms.map((meetingRoom: MeetingRoom, key: number) => (
                  <DailyMeetingRoomSchedule key={key} className={"grid-col"} meetingRoom={meetingRoom} calendarSyncStatus={calendarSyncStatus} />
                ))}
              {isViewingToDay && <CurrentTimeLine percentageThroughDay={percentageThroughDay} roomCount={meetingRooms.length}/>}
            </Grid>
          </GridContainer>
        </>
      </>}
    </>
  )
}

interface Props {
  calendarSyncStatus: SyncStatus;
}

const CurrentTimeLine = styled.div<any>`
  position: absolute;
  top: ${props => (HALF_HOUR_TIME_SLOT_HEIGHT * 48) * props.percentageThroughDay + MEETING_ROOM_HEADER_HEIGHT}px;
  min-width: 100%;
  width: ${props => props.roomCount * MEETING_ROOM_SWIMLANE_WIDTH + 110}px;
  background: red;
  height: 1px;
`

const GridContainer = styled.div`
  display: grid; /* This is a (hacky) way to make the .grid element size to fit its content */
  position: relative;
  flex: 1;
  max-width: 100% !important;
`
